/**
 * Two pieces of table furniture, drawn from engine state only.
 *
 * The *plate* lies in the middle of the discard field, the way a real table has
 * a marker in the middle: the four winds around its rim, the seat to move lit,
 * and the number of tiles still standing in the wall at its centre.
 *
 * The *plaque* is inlaid into the near rail and spells the same position out in
 * words, because a beginner should never have to infer whose turn it is from a
 * highlight. It states the three facts that are only available as numbers -
 * whose turn it is, how much wall is left, and what was discarded last - and
 * there is no score, no round counter and no invented statistic, because this
 * milestone deliberately has none.
 *
 * What the table is *waiting for* is deliberately not repeated here: the action
 * bar below the felt says it in a full sentence, one line away, and a rail that
 * echoes the sentence above it is how a table starts to read as a dashboard.
 * The phase is still carried as `data-phase`, so automation never has to parse
 * a translated word.
 *
 * The compass is drawn from *your* point of view: you always sit at the
 * bottom, the seat that plays after you sits on the right, and the seat you
 * may chow from sits on the left, which is exactly how the felt is laid out.
 */

import type { PlayerId } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";
import { seatSide } from "../game/tableModel";
import type { SeatSide } from "../game/tableModel";
import { MahjongTile } from "./tiles";

export interface CenterBoardProps {
  model: TableModel;
  nameFor: (playerId: PlayerId) => string;
  /** Mode-specific note, e.g. the multiplayer room code. */
  extra?: string;
}

const COMPASS_ORDER: readonly SeatSide[] = ["south", "east", "north", "west"];

export function CenterBoard({ model, nameFor, extra }: CenterBoardProps) {
  const { t, tile, seat: seatWind } = useI18n();
  const ended = model.result !== null;
  const claiming = model.phase === "awaiting-claims";

  return (
    <section className="center" data-testid="center-board" aria-label={t("table.center")}>
      <div className="compass" aria-hidden="true" title={t("table.compass")}>
        <span className="compass__ring" />
        <span className="compass__rim" />
        {COMPASS_ORDER.map((side) => {
          const seat = model.seats.find((candidate) => seatSide(candidate.id, model.you) === side);
          if (!seat) return null;
          // During a claim window nobody is "to move": the table is waiting
          // on whoever may still claim, and that is what the ring shows.
          const isTurn = !ended && !claiming && model.turn === seat.id;
          const isClaiming =
            claiming && (model.pendingClaims?.waitingOn.includes(seat.id) ?? false);
          return (
            <span
              key={side}
              className={[
                "compass__point",
                `compass__point--${side}`,
                isTurn ? "compass__point--turn" : "",
                isClaiming ? "compass__point--claim" : "",
                seat.isYou ? "compass__point--you" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-side={side}
              data-turn={isTurn ? "true" : "false"}
              data-claiming={isClaiming ? "true" : "false"}
              data-dealer={seat.id === model.dealer ? "true" : "false"}
              title={seatWind(seat.seat)}
            >
              {/*
               * One letter, because the plate is the size of a real table
               * marker and four wind words will not fit inside it without
               * colliding with the wall count at its centre. The whole plate
               * is decorative and aria-hidden: the wind, the dealer and whose
               * turn it is are all spelled out in words on the seat plates.
               */}
              <span className="compass__wind">{seatWind(seat.seat).slice(0, 1)}</span>
            </span>
          );
        })}
        <span className="compass__hub">{model.wallRemaining}</span>
        <span className="compass__hub-label">{t("status.wallShort")}</span>
      </div>

      {/*
        * The position in words. It is a live region for anyone who cannot read
        * the table, and it is not drawn: a strip along the rail repeating what
        * the felt already shows is exactly the "nested widget" that made the
        * centre of this game look like a dashboard. Every fact in it is
        * physically present a few centimetres away - the lit seat plate and
        * compass point for the turn, the count engraved in the plate for the
        * wall, the freshest tile in the discard field for the last discard.
        */}
      <div
        className="status visually-hidden"
        aria-live="polite"
        // Language-neutral values, for tests and future automation.
        data-phase={model.phase}
        data-turn={model.turn}
        data-wall={model.wallRemaining}
      >
        <span className="status__row status__row--turn">
          {t("status.turn")}: <strong>{ended ? t("status.ended") : nameFor(model.turn)}</strong>
        </span>
        <span className="status__row">
          {t("status.wall")}: <strong>{model.wallRemaining}</strong>
        </span>
        <span className="status__row status__last">
          {t("status.lastDiscard")}:{" "}
          {model.lastDiscard ? (
            <>
              <MahjongTile kind={model.lastDiscard.tile.kind} size="sm" />
              <span className="status__last-text">
                {t("status.discardBy", {
                  tile: tile(model.lastDiscard.tile.kind),
                  player: nameFor(model.lastDiscard.playerId),
                })}
              </span>
            </>
          ) : (
            <strong>{t("status.none")}</strong>
          )}
        </span>
        {extra ? <span className="status__extra">{extra}</span> : null}
      </div>
    </section>
  );
}
